import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, Search } from 'lucide-react';
import BlogCard from '../components/BlogCard';
import { posts, categories } from '../data/blog';
import './Projects.css';
import './Blog.css';

const SITE = 'https://www.pidesk.in';
const PAGE_SIZE = 6;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

const Blog = () => {
  const [active, setActive] = useState('All');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => [...posts].sort((a, b) => new Date(b.date) - new Date(a.date)), []);
  const featured = sorted[0];
  const rest = sorted.slice(1);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rest.filter((p) => {
      const inCategory = active === 'All' || p.category === active;
      const inQuery = !q || p.title.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q) || p.tags.some((t) => t.toLowerCase().includes(q));
      return inCategory && inQuery;
    });
  }, [rest, active, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const shown = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const setFilter = (next) => { setActive(next); setPage(1); };
  const setSearch = (val) => { setQuery(val); setPage(1); };

  const counts = useMemo(() => {
    const c = {};
    posts.forEach((p) => { c[p.category] = (c[p.category] || 0) + 1; });
    return c;
  }, []);

  const recent = sorted.slice(0, 5);

  const listSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Pidesk Blog',
    url: `${SITE}/blog`,
    blogPost: sorted.map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      url: `${SITE}/blog/${p.slug}`,
      datePublished: p.date,
      image: `${SITE}${p.cover}`
    }))
  };

  return (
    <>
      <>
        <title>Blog - College ERP, School Management & EdTech Guides | Pidesk</title>
        <meta
          name="description"
          content="Guides on college ERP software, school management systems, online test platforms and learning apps, from the team at Pidesk."
        />
        <link rel="canonical" href={`${SITE}/blog`} />
        <script type="application/ld+json">{JSON.stringify(listSchema)}</script>
      </>

      <div className="page-shell">
        <div className="container">
          <div className="page-hero">
            <span className="projects-eyebrow">Blog</span>
            <h1 className="page-title">Guides on ERP, exams &amp; EdTech</h1>
            <p className="page-subtitle">
              Practical guides on college ERP, school management, online testing and learning apps,
              written by the team building them.
            </p>
          </div>

          {featured && (
            <Link to={`/blog/${featured.slug}`} className={`blog-feature tone-${featured.tone}`}>
              <img src={featured.cover} alt={featured.coverAlt} width="1200" height="630" loading="eager" fetchPriority="high" />
              <div className="blog-feature-body">
                <span className="blog-feature-badge">Latest</span>
                <span className="blog-card-cat">{featured.category}</span>
                <h2>{featured.title}</h2>
                <p>{featured.excerpt}</p>
                <div className="blog-card-meta">
                  <span><Calendar size={13} /> {formatDate(featured.date)}</span>
                  <span><Clock size={13} /> {featured.readTime}</span>
                </div>
                <span className="link-arrow" style={{ marginTop: 14 }}>Read article <ArrowRight size={16} /></span>
              </div>
            </Link>
          )}

          <div className="blog-toolbar">
            <div className="blog-search">
              <Search size={16} />
              <input
                type="search"
                placeholder="Search articles…"
                value={query}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search blog posts"
              />
            </div>
            <div className="blog-filters">
              <button type="button" className={`blog-filter ${active === 'All' ? 'on' : ''}`} onClick={() => setFilter('All')}>
                All posts
              </button>
              {categories.map((c) => (
                <button key={c} type="button" className={`blog-filter ${active === c ? 'on' : ''}`} onClick={() => setFilter(c)}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="blog-layout">
            <div>
              {shown.length > 0 ? (
                <div className="blog-grid">
                  {shown.map((post) => <BlogCard key={post.slug} post={post} />)}
                </div>
              ) : (
                <div className="shots-empty">No articles match that search yet.</div>
              )}

              {totalPages > 1 && (
                <div className="blog-pagination">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`page-btn ${n === page ? 'on' : ''}`}
                      onClick={() => setPage(n)}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <aside className="post-side">
              <div className="side-box">
                <h3>Categories</h3>
                <ul className="cat-list">
                  {categories.map((c) => (
                    <li key={c}>
                      <button type="button" onClick={() => setFilter(c)} className={active === c ? 'on' : ''}>
                        {c} <span>{counts[c]}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="side-box">
                <h3>Recent posts</h3>
                <ul className="other-roles">
                  {recent.map((p) => (
                    <li key={p.slug}>
                      <Link to={`/blog/${p.slug}`}>
                        <span>{p.title}</span>
                        <ArrowRight size={14} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="side-box">
                <h3>Have a project in mind?</h3>
                <p>We build the ERP, exam and learning platforms this blog covers.</p>
                <Link to="/contact" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>Start a project</Link>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default Blog;
